// === Admin: Bug Reports & Feature Requests ===
// View, filter, update status, and delete all company-submitted reports.

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Stack,
  Drawer,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  CheckCircle as DoneIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  Lightbulb as FeatureIcon,
  OpenInNew as LinkIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ReportType = 'bug' | 'feature';
type Priority   = 'low' | 'medium' | 'high' | 'critical';
type Status     = 'open' | 'in_progress' | 'completed' | 'wont_fix';

interface BugReport {
  readonly id:                 number;
  readonly type:               ReportType;
  readonly section:            string;
  readonly priority:           Priority;
  readonly title:              string;
  readonly description:        string;
  readonly screenshot_url:     string | null;
  readonly status:             Status;
  readonly company_id:         string;
  readonly company_name?:       string;
  readonly reported_by_name:   string | null;
  readonly reported_by_email:  string | null;
  readonly completed_by:       string | null;
  readonly completed_at:       string | null;
  readonly created_at:         string;
}

interface ListResponse {
  readonly data:  readonly BugReport[];
  readonly total: number;
  readonly page:  number;
  readonly limit: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRIORITY_COLORS: Record<Priority, 'default' | 'info' | 'warning' | 'error'> = {
  low:      'default',
  medium:   'info',
  high:     'warning',
  critical: 'error',
};

const STATUS_COLORS: Record<Status, 'default' | 'info' | 'success' | 'warning'> = {
  open:        'warning',
  in_progress: 'info',
  completed:   'success',
  wont_fix:    'default',
};

const STATUS_LABELS: Record<Status, string> = {
  open:        'Open',
  in_progress: 'In Progress',
  completed:   'Completed',
  wont_fix:    "Won't Fix",
};

const ROWS_PER_PAGE = 25;

// ---------------------------------------------------------------------------
// BugReportsPage
// ---------------------------------------------------------------------------

export function BugReportsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // ---- List state ----
  const [rows, setRows]       = useState<readonly BugReport[]>([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Filters ----
  const [search, setSearch]         = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('open');
  const [filterPriority, setFilterPriority] = useState('');

  // ---- Detail drawer ----
  const [selected, setSelected] = useState<BugReport | null>(null);

  // ---- Delete confirm ----
  const [deleteTarget, setDeleteTarget] = useState<BugReport | null>(null);
  const [deleting, setDeleting]         = useState(false);

  // ---- Status update ----
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---------------------------------------------------------------------------
  // Load list
  // ---------------------------------------------------------------------------
  const loadList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(ROWS_PER_PAGE) });
      if (search)         params.set('search', search);
      if (filterType)     params.set('type', filterType);
      if (filterStatus)   params.set('status', filterStatus);
      if (filterPriority) params.set('priority', filterPriority);

      const res = await fetch(`${getApiUrl()}/api/v1/admin/bug-reports/admin?${params}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as ListResponse;
      setRows(body.data);
      setTotal(body.total);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [token, page, search, filterType, filterStatus, filterPriority]);

  useEffect(() => { void loadList(); }, [loadList]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // ---------------------------------------------------------------------------
  // Update status
  // ---------------------------------------------------------------------------
  const updateStatus = async (id: number, status: Status) => {
    setUpdatingId(id);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/bug-reports/admin/${id}`, {
        method:  'PATCH',
        headers: authHeaders(token),
        body:    JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showSnack('Status updated');
      void loadList();
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      showSnack(err instanceof Error ? err.message : 'Update failed', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/bug-reports/admin/${deleteTarget.id}`, {
        method:  'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showSnack('Report deleted');
      setDeleteTarget(null);
      if (selected?.id === deleteTarget.id) setSelected(null);
      void loadList();
    } catch (err) {
      showSnack(err instanceof Error ? err.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const pageCount = Math.ceil(total / ROWS_PER_PAGE);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton onClick={() => navigate('/dashboard')} size="small">
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700}>Bugs / New Features</Typography>
          <Typography color="text.secondary">Review and manage all submitted reports</Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => { void loadList(); }} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search title / description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select value={filterType} label="Type" onChange={(e) => { setPage(1); setFilterType(e.target.value); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="bug">Bug</MenuItem>
              <MenuItem value="feature">Feature</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={(e) => { setPage(1); setFilterStatus(e.target.value); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="wont_fix">Won't Fix</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={filterPriority} label="Priority" onChange={(e) => { setPage(1); setFilterPriority(e.target.value); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
          {(filterType || filterStatus || filterPriority || search) && (
            <Button variant="outlined" size="small" onClick={() => {
              setSearch(''); setFilterType(''); setFilterStatus(''); setFilterPriority(''); setPage(1);
            }}>
              Clear
            </Button>
          )}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {total} report{total !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Type</TableCell>
              <TableCell>Section</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Completed</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No reports found.</Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((r, idx) => (
              <TableRow
                key={r.id}
                hover
                onClick={() => setSelected(r)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: idx % 2 !== 0 ? alpha('#000', 0.02) : 'transparent',
                }}
              >
                <TableCell>
                  <Chip
                    size="small"
                    icon={r.type === 'bug' ? <BugIcon /> : <FeatureIcon />}
                    label={r.type === 'bug' ? 'Bug' : 'Feature'}
                    color={r.type === 'bug' ? 'error' : 'info'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{r.section}</TableCell>
                <TableCell sx={{ maxWidth: 300 }}>
                  <Typography variant="body2" noWrap title={r.title}>{r.title}</Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={r.priority} color={PRIORITY_COLORS[r.priority]} />
                </TableCell>
                <TableCell>
                  <Chip size="small" label={STATUS_LABELS[r.status]} color={STATUS_COLORS[r.status]} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{r.company_name ?? r.company_id}</Typography>
                </TableCell>
                <TableCell>
                  {new Date(r.created_at).toLocaleDateString('en-AU')}
                </TableCell>
                <TableCell>
                  {r.completed_at
                    ? new Date(r.completed_at).toLocaleDateString('en-AU')
                    : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {r.status !== 'completed' && (
                      <Tooltip title="Mark Complete">
                        <IconButton
                          size="small"
                          color="success"
                          disabled={updatingId === r.id}
                          onClick={() => void updateStatus(r.id, 'completed')}
                        >
                          {updatingId === r.id ? <CircularProgress size={16} /> : <DoneIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)} color="primary" />
        </Box>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Detail Drawer                                                       */}
      {/* ----------------------------------------------------------------- */}
      <Drawer
        anchor="right"
        open={selected !== null}
        onClose={() => setSelected(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 3 } }}
      >
        {selected && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>Report Detail</Typography>
              <Chip
                size="small"
                icon={selected.type === 'bug' ? <BugIcon /> : <FeatureIcon />}
                label={selected.type === 'bug' ? 'Bug' : 'Feature'}
                color={selected.type === 'bug' ? 'error' : 'info'}
                variant="outlined"
              />
            </Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>{selected.title}</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip size="small" label={selected.priority} color={PRIORITY_COLORS[selected.priority]} />
              <Chip size="small" label={STATUS_LABELS[selected.status]} color={STATUS_COLORS[selected.status]} />
            </Stack>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Section:</strong> {selected.section}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Company:</strong> {selected.company_name ?? selected.company_id}
            </Typography>
            {selected.reported_by_name && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Reported by:</strong> {selected.reported_by_name}
                {selected.reported_by_email ? ` (${selected.reported_by_email})` : ''}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Submitted:</strong> {new Date(selected.created_at).toLocaleString('en-AU')}
            </Typography>
            {selected.completed_at && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Completed:</strong> {new Date(selected.completed_at).toLocaleString('en-AU')}
                {selected.completed_by ? ` by ${selected.completed_by}` : ''}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
              {selected.description}
            </Typography>

            {selected.screenshot_url && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Screenshot</Typography>
                <Box
                  component="img"
                  src={selected.screenshot_url}
                  alt="screenshot"
                  sx={{ width: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 1 }}
                />
                <Button
                  size="small"
                  startIcon={<LinkIcon />}
                  href={selected.screenshot_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open full size
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Reply via email */}
            {selected.reported_by_email && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Reply</Typography>
                <Button
                  size="small"
                  variant="contained"
                  href={(() => {
                    const submitted = new Date(selected.created_at).toLocaleString('en-AU');
                    const subject = `Re: [Click2Gro] ${selected.type === 'bug' ? 'Bug' : 'Feature'} — ${selected.title}`;
                    const greeting = selected.reported_by_name ? `Hi ${selected.reported_by_name},\n\n` : 'Hi,\n\n';
                    const quoted = [
                      '',
                      '---',
                      `Section: ${selected.section}`,
                      `Submitted: ${submitted}`,
                      `Title: ${selected.title}`,
                      '',
                      'Issue reported:',
                      selected.description,
                    ].join('\n');
                    const body = `${greeting}\n\n${quoted}\n\nRegards,\nClick2Gro Support`;
                    return `mailto:${encodeURIComponent(selected.reported_by_email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  })()}
                >
                  Reply via email
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Status actions */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Update Status</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {(['open', 'in_progress', 'completed', 'wont_fix'] as const).map((s) => (
                <Button
                  key={s}
                  size="small"
                  variant={selected.status === s ? 'contained' : 'outlined'}
                  color={STATUS_COLORS[s] === 'default' ? 'inherit' : STATUS_COLORS[s]}
                  disabled={selected.status === s || updatingId === selected.id}
                  onClick={() => void updateStatus(selected.id, s)}
                  sx={{ mb: 1 }}
                >
                  {STATUS_LABELS[s]}
                </Button>
              ))}
            </Stack>
          </Box>
        )}
      </Drawer>

      {/* ----------------------------------------------------------------- */}
      {/* Delete confirm                                                      */}
      {/* ----------------------------------------------------------------- */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Report?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "<strong>{deleteTarget?.title}</strong>"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => { void confirmDelete(); }}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
